import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import clsx from 'clsx';
import * as actions from 'containers/App/actions';
import APModal from 'components/Modal/APModal';
import { authAccountWithIntegration } from 'utils/integrations';
import Cards from 'components/Cards';
import TypeCard from 'components/Cards/TypeCard';
import CalendarImage from 'assets/images/calendar.svg';
import GmailImage from 'assets/images/gmail.svg';
import iCloudImage from 'assets/images/icloud.svg';
import MicrosoftImage from 'assets/images/outlook.png';

const CalendarIntegration = () => {
  const userInfo = useSelector((state) => state.global.userInfo);
  const { integrations, integrationDeleting } = userInfo;

  const [type, setType] = useState('');
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  const integrationsNonVirtual = useMemo(() => integrations
    .filter(integration => {
      return integration.grantStatus !== 'disconnected' && integration.provider !== 'virtual-calendar';
    }), [integrations]);

  const integrationsNonExpired = useMemo(() => {
    return integrationsNonVirtual.filter(int => int.grantStatus !== 'expired');
  }, [integrationsNonVirtual]);

  const isConnected = integrationsNonExpired && integrationsNonExpired.length > 0;

  // TODO: At some point we should address integrations via their id.
  // For now, we're just going to assume that there's only one integration.
  // Relevant variables:
  // - integrations
  // - integrationDeleting
  // - integrationsNonVirtual
  // - isIntegrationDeleting
  const isIntegrationDeleting = Object.values(integrationDeleting).some(value => value);

  const dispatch = useDispatch();
  const {
    deleteIntegrationAccount,
  } = bindActionCreators(actions, dispatch);

  const handleIntegrationConnect = () => {
    // Connect a new account.
    if (!integrationsNonVirtual || integrationsNonVirtual.length < 1) {
      setConnectModalOpen(true);
      return;
    }
    // Re-integration an existing account.
    const expiredIntegration = integrations.find(int => int.grantStatus === 'expired');
    if (expiredIntegration) {
      const { provider, email } = expiredIntegration;
      authAccountWithIntegration({ provider, loginHint: email }, setErrorModal);
    }
  };

  const setErrorModal = (message) => {
    setErrorModalMessage(message);
    setErrorModalOpen(true);
  };

  const errorModalCloseHandler = () => {
    setErrorModalOpen(false);
    setErrorModalMessage(null);
  };

  const getProviderImage = (provider) => {
    switch (provider) {
      case 'google':
        return GmailImage;
      case 'icloud':
        return iCloudImage;
      case 'microsoft':
        return MicrosoftImage;
      default:
        return false;
    }
  };

  return (
    <>
      <div className="integration">
        <div className="integration__image calendar">
          <img src={CalendarImage} />
        </div>
        <div className="integration__content">
          <h5 className="integration__title">CALENDAR</h5>
          <p>
            Connect your Google, Apple, or Microsoft calendar to enable Aisle Planner’s online
            scheduling feature. Once connected, you’ll be able to create and share scheduling
            links to easily book meetings and events based on your availability.
            <br />
            <a href="http://help.aisleplanner.com/en/articles/9981556-an-intro-to-aisle-planner-s-scheduling-assistant"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </a>.
          </p>
          {integrationsNonVirtual.length ? (
            <div className={clsx('connected-provider', integrationsNonVirtual[0].provider)}>
              <img src={getProviderImage(integrationsNonVirtual[0].provider)} />
              <span>Your connected calendar: <strong>{integrationsNonVirtual[0].email}</strong></span>
              <a href="/app/v2/scheduler">Manage</a>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setConfirmModalOpen(true);
                }}
                disabled={isIntegrationDeleting}
              >
                Disconnect
              </a>
            </div>
          ) : null}
        </div>
        <div className="integration__actions">
          <button
            className={clsx('button', (isIntegrationDeleting || isConnected) && 'button--connected')}
            onClick={handleIntegrationConnect}
          >
            {
              isIntegrationDeleting ?
                'Loading...' :
              integrationsNonVirtual.length ?
                'Connected' : 'Connect'
            }
          </button>
        </div>
      </div>

      <APModal
        open={connectModalOpen}
        title="Connect your calendar."
        subtitle="Select an external calendar"
        name="connect-provider"
        size="medium"
        onClose={() => setConnectModalOpen(false)}
      >
        <Cards>
          <TypeCard
            selected={type === 'google'}
            name="gmail"
            title="Google Calendar"
            img={{
              imgUrl: GmailImage,
              alt: 'Google Calendar',
            }}
            onClick={() => setType('google')}
          />
          <TypeCard
            selected={type === 'icloud'}
            name="icloud"
            title="Apple iCloud"
            img={{
              imgUrl: iCloudImage,
              alt: 'Apple iCloud',
            }}
            onClick={() => setType('icloud')}
          />
          <TypeCard
            selected={type === 'microsoft'}
            name="microsoft-outlook"
            title="Microsoft Outlook"
            img={{
              imgUrl: MicrosoftImage,
              alt: 'Microsoft Outlook',
            }}
            onClick={() => setType('microsoft')}
          />
        </Cards>
        <button
          className="button"
          onClick={() => authAccountWithIntegration(
            { 'provider': type, 'baseUrl': window.location.origin },
            setErrorModal,
          )}
          disabled={!type}
        >
          Continue
        </button>
      </APModal>

      <APModal
        open={errorModalOpen}
        onClose={errorModalCloseHandler}
        actions={(
          <button
            onClick={errorModalCloseHandler}
            className="button"
          >
            Ok
          </button>
        )}
      >
        <div>{errorModalMessage}</div>
      </APModal>

      <APModal
        title="Disconnect Your External Account"
        name="confirm-disconnect"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        actions={(
          <>
            <button
              onClick={() => setConfirmModalOpen(false)}
              className="button button--cancel"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                deleteIntegrationAccount(integrationsNonVirtual[0].id);
                setConfirmModalOpen(false);
              }}
              className="button"
            >
              Disconnect
            </button>
          </>
        )}
      >
        <p>
          Your scheduling, calendar and email integrations are all enabled by the external account you linked to Aisle
          Planner. By disconnecting, any features enabled by this integration will no longer be active. Are you sure you
          want to disconnect?
        </p>
      </APModal>
    </>
  );
};

export default CalendarIntegration;
